import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} carousel - The carousel element.
 * @property {HTMLElement[]} media - The media elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'carousel', 'media'];

  /** @type {import('fancyapps').Carousel | null} */
  #carousel = null;

  /** @type {number | null} */
  #carouselWaitFrame = null;

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  #activeIndex = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#carouselWaitFrame !== null) {
      cancelAnimationFrame(this.#carouselWaitFrame);
      this.#carouselWaitFrame = null;
    }

    this.#carousel?.off('ready change settle', this.#handleCarouselChange);
    this.#carousel?.destroy();
    this.#carousel = null;
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media } = this.refs;
    const targetImage = media[index];

    if (!targetImage) return;

    const open = () => {
      dialog.showModal();
      this.#selectMedia(index);
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');
  }

  /**
   * Selects media in the full-screen carousel, initializing it on first use.
   * @param {number} index - The media index to select.
   */
  #selectMedia(index) {
    if (!this.#initializeCarousel(index)) {
      this.refs.media.forEach((mediaItem, mediaIndex) => {
        mediaItem.hidden = mediaIndex !== index;
      });
      this.#setActiveMedia(index);
      this.#waitForCarousel(index);
      return;
    }

    this.#carousel?.updateMetrics();
    this.#carousel?.slideTo(index, { friction: 0, transition: false });
    this.#setActiveMedia(index);
  }

  /**
   * Initializes the existing FancyApps carousel used throughout the theme.
   * @param {number} initialIndex - The initially selected media index.
   * @returns {boolean} Whether the carousel is ready.
   */
  #initializeCarousel(initialIndex) {
    if (this.#carousel) return true;
    if (!window.Carousel) return false;

    const { carousel, media } = this.refs;

    media.forEach((mediaItem) => {
      mediaItem.hidden = false;
    });

    this.#carousel = new window.Carousel(carousel, {
      Dots: false,
      Navigation: true,
      initialPage: initialIndex,
      infinite: true,
      center: false,
      fill: false,
      slidesPerPage: 1,
      l10n: {
        NEXT: carousel.dataset.nextLabel || 'Next slide',
        PREV: carousel.dataset.previousLabel || 'Previous slide',
      },
    });

    this.#carousel.on('ready change settle', this.#handleCarouselChange);
    return true;
  }

  /**
   * Waits for the globally loaded carousel script when the dialog is opened
   * immediately after page load.
   * @param {number} index - The media index to select after initialization.
   */
  #waitForCarousel(index) {
    if (this.#carouselWaitFrame !== null) return;

    const initialize = () => {
      this.#carouselWaitFrame = null;

      if (!this.isConnected || !this.refs.dialog.open) return;

      if (this.#initializeCarousel(index)) {
        this.#carousel?.updateMetrics();
        this.#carousel?.slideTo(index, { friction: 0, transition: false });
        this.#setActiveMedia(index);
        return;
      }

      this.#carouselWaitFrame = requestAnimationFrame(initialize);
    };

    this.#carouselWaitFrame = requestAnimationFrame(initialize);
  }

  #handleCarouselChange = () => {
    const currentPage = this.#carousel?.pages?.[this.#carousel.page];
    const index = currentPage?.slides?.[0]?.index ?? this.#carousel?.page;

    if (typeof index === 'number') this.#setActiveMedia(index);
  };

  /**
   * Updates accessibility, high-resolution loading and the underlying gallery.
   * @param {number} index - The selected media index.
   */
  #setActiveMedia(index) {
    const { media } = this.refs;
    const targetMedia = media[index];

    if (!targetMedia) return;

    this.#activeIndex = index;

    media.forEach((mediaItem, mediaIndex) => {
      const isActive = mediaIndex === index;
      mediaItem.setAttribute('aria-hidden', String(!isActive));
      mediaItem.toggleAttribute('inert', !isActive);

      if (!isActive) {
        mediaItem.querySelectorAll('deferred-media, product-model').forEach((deferredMedia) => {
          if ('pauseMedia' in deferredMedia && typeof deferredMedia.pauseMedia === 'function') {
            deferredMedia.pauseMedia();
          }
        });
      }
    });

    this.loadHighResolutionImage(targetMedia);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const activeIndex = this.#activeIndex;
    const mostVisibleElement = media[activeIndex];

    if (!mostVisibleElement) return this.closeDialog();

    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'ArrowLeft':
        if (!this.#carousel) return;
        event.preventDefault();
        document.dir === 'rtl' ? this.#carousel.slideNext() : this.#carousel.slidePrev();
        break;
      case 'ArrowRight':
        if (!this.#carousel) return;
        event.preventDefault();
        document.dir === 'rtl' ? this.#carousel.slidePrev() : this.#carousel.slideNext();
        break;
    }
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
